import { parseEnumFilter, parseNumberFilter } from '@/utils/filters';
import { APPROVAL_STATUS, LEAVE_REQUEST_TYPE } from '../constants/leaveApplication.constants';
import type { LeaveApplicationFilters, LeaveApplicationSearch } from '../types/filters.types';
import type {
  LeaveApplicationCountsBySubmitterParams,
  LeaveApplicationSummaryParams,
  LeaveRequestType,
} from '../types/leaveApplication.types';

const ATTENDANCE_CLAIM: LeaveRequestType = 'ATTENDANCE_CLAIM';

const FILTER_KEY_BY_SEARCH_KEY = [
  ['dateFrom', 'dateFrom'],
  ['dateTo', 'dateTo'],
  ['leaveStatus', 'status'],
  ['requestType', 'requestType'],
  ['leaveTypeId', 'leaveTypeId'],
  ['amUserId', 'amUserId'],
  ['zmUserId', 'zmUserId'],
  ['divisionId', 'divisionId'],
  ['districtId', 'districtId'],
  ['thanaId', 'thanaId'],
] as const satisfies readonly (readonly [keyof LeaveApplicationSearch, string])[];

export function toLeaveApplicationFilters(search: LeaveApplicationSearch): LeaveApplicationFilters {
  return Object.fromEntries(
    FILTER_KEY_BY_SEARCH_KEY.flatMap(([searchKey, filterKey]) => {
      const value = search[searchKey];
      return value === undefined ? [] : [[filterKey, String(value)]];
    }),
  );
}

export function toLeaveApplicationSearch(
  filters: LeaveApplicationFilters,
  searchText: string,
): LeaveApplicationSearch {
  const { status, ...params } = toSummaryParams(filters, searchText);
  return { ...params, leaveStatus: status };
}

export function resolveDateRange(filters: LeaveApplicationFilters): {
  dateFrom: string;
  dateTo: string;
} {
  return { dateFrom: filters.dateFrom ?? '', dateTo: filters.dateTo ?? '' };
}

export function isAttendanceClaimSelected(filters: LeaveApplicationFilters): boolean {
  return filters.requestType === ATTENDANCE_CLAIM;
}

export function clearDependentFilters(
  previous: LeaveApplicationFilters,
  next: LeaveApplicationFilters,
): LeaveApplicationFilters {
  const amChanged = next.amUserId !== previous.amUserId;
  const divisionChanged = next.divisionId !== previous.divisionId;
  const districtChanged = divisionChanged || next.districtId !== previous.districtId;

  return {
    ...next,
    ...(amChanged ? { zmUserId: '' } : {}),
    ...(divisionChanged ? { districtId: '' } : {}),
    ...(districtChanged ? { thanaId: '' } : {}),
    ...(isAttendanceClaimSelected(next) ? { leaveTypeId: '' } : {}),
  };
}

export function toSummaryParams(
  filters: LeaveApplicationFilters,
  searchText: string,
): LeaveApplicationSummaryParams {
  const { dateFrom, dateTo } = resolveDateRange(filters);
  return {
    status: parseEnumFilter(filters.status, APPROVAL_STATUS),
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    requestType: parseEnumFilter(filters.requestType, LEAVE_REQUEST_TYPE),
    leaveTypeId: parseNumberFilter(filters.leaveTypeId),
    amUserId: parseNumberFilter(filters.amUserId),
    zmUserId: parseNumberFilter(filters.zmUserId),
    divisionId: parseNumberFilter(filters.divisionId),
    districtId: parseNumberFilter(filters.districtId),
    thanaId: parseNumberFilter(filters.thanaId),
    search: searchText.trim() || undefined,
  };
}

export function toCountsBySubmitterParams(
  summaryParams: LeaveApplicationSummaryParams,
): LeaveApplicationCountsBySubmitterParams {
  return {
    status: summaryParams.status,
    dateFrom: summaryParams.dateFrom,
    dateTo: summaryParams.dateTo,
    requestType: summaryParams.requestType,
    leaveTypeId: summaryParams.leaveTypeId,
    divisionId: summaryParams.divisionId,
    districtId: summaryParams.districtId,
    thanaId: summaryParams.thanaId,
    search: summaryParams.search,
  };
}
